import { StepKey, useSteps } from '../contexts/stepContext';
import { useAppTranslation } from './useAppTranslation';

export interface WizardStep {
  label: string;
  /** Valor de currentStep em que esta etapa aparece. */
  step: number;
}

interface UseWizardSteps {
  steps: WizardStep[];
  /** Índice da etapa atual em `steps` (-1 antes da primeira). */
  currentIndex: number;
}

/**
 * Monta a lista de etapas exibida no indicador, em cima da mesma ordem que o
 * `changeStep` usa para navegar (`flowSteps` do stepContext) — sem repetir a
 * numeração na mão.
 *
 * O indicador é progressivo: enquanto o método de análise não foi escolhido,
 * só aparecem as etapas já certas (seleção de serviços e a escolha do método).
 * As posteriores entram depois da escolha, e instruções/foto ficam de fora
 * quando a análise é por preenchimento de dados. Como o `Choose` limpa a
 * escolha ao ser remontado, voltar até ele recolhe o indicador de novo.
 */
export function useWizardSteps(): UseWizardSteps {
  const {
    currentStep,
    currentValidation,
    skipPhotoRequested,
    flowSteps,
    personType,
  } = useSteps();
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(tr => tr.components);

  const withoutPhoto = currentValidation === 'Dados' || skipPhotoRequested;

  // A etapa de método existe sempre agora (com biometria facial ela pergunta
  // só o tipo de foto; sem biometria, foto x preenchimento de dados), então o
  // indicador só sai do estado recolhido depois que o atendente escolhe —
  // não há mais o caso "não existe etapa de método".
  const methodChosen = currentValidation !== '' || skipPhotoRequested;

  const labels: Record<StepKey, string> = {
    services: tp('servicesSelection.title'),
    method: tp('choose.title'),
    identification: tp('identification.title'),
    company: tp('companyIdentification.title'),
    additional: tp('additionalFields.title'),
    instructions: tp('instructions.title'),
    camera: tp('camera.title'),
    result: tp('result.title'),
  };

  const isVisible = (key: StepKey): boolean => {
    // O fluxo de Pessoa Jurídica não tem método nem foto: as etapas dele são
    // conhecidas desde o início (serviços, CNPJ, resultado).
    if (personType === 'PJ') return true;
    if (!methodChosen) return key === 'services' || key === 'method';
    if (withoutPhoto && (key === 'instructions' || key === 'camera')) {
      return false;
    }
    return true;
  };

  const steps: WizardStep[] = flowSteps
    .map((key, index) => ({ key, label: labels[key], step: index + 1 }))
    .filter(item => isVisible(item.key))
    .map(({ label, step }) => ({ label, step }));

  const currentIndex =
    steps.filter(item => item.step <= currentStep).length - 1;

  return { steps, currentIndex };
}
